import type { CabinetProduct } from '../data/cabinetCatalogue';
import type { HandleProduct, Piece } from '../doors/drilling';
import { newPiece } from '../doors/drilling';
import { limitsKey, newCabinet, RULES, type Cabinet } from './model';

/**
 * Is a made-to-measure cabinet one of the shop's standard units, left as it is?
 *
 * <p>The owner's rule (2026-09-28): a standard cabinet has a price; a modified
 * one is priced on request. So a cabinet in the made-to-measure tab that is
 * exactly a published standard unit is sold as that unit, at its price, and
 * only a changed one goes to a price request. Nothing is priced by formula.
 *
 * <p>"Exactly" is deliberately strict, because a wrong match sells the wrong
 * carcass. Each builder type is matched only in its own group of the range,
 * on the same width, height and depth, and built rigid:
 * <ul>
 *   <li>base, wall, wardrobe: one row of full-height doors, as many as chosen;</li>
 *   <li>tall: upper and lower doors with the same upper door height, or
 *       full-height doors, as many across as chosen;</li>
 *   <li>drawers: the same number of full-width fronts;</li>
 *   <li>L-shaped corner base: the same width along each wall and arm depth;</li>
 *   <li>blind corner base: the same blank section, on the same side;</li>
 *   <li>diagonal wall corner: the same width along the wall and arm depth.</li>
 * </ul>
 * The inside counts too: shelves or drawer heights set by hand make it made to
 * measure. A shelf count left at the builder's default takes the unit's own
 * shelves; where two units differ only in their shelves (a broom and a larder
 * unit), the one with the chosen count is taken. Under-stairs runs are never
 * standard.
 */

/** The standard range's group each builder type is matched in. */
const GROUP_FOR: Record<string, string> = {
  base: 'base',
  wall: 'wall',
  tall: 'tall',
  drawers: 'drawers',
  wardrobe: 'wardrobe',
  'corner-l': 'corner-lshape',
  'corner-blind': 'corner',
  'corner-diag': 'corner-wall-diagonal',
};

export function standardGroupFor(c: Cabinet): string | undefined {
  return GROUP_FOR[limitsKey(c.type, c.corner)];
}

function defaultShelfCount(c: Cabinet): number {
  return newCabinet(c.type, c.corner).shelves.length;
}

/** Rigid, and no shelf or drawer height set by hand. */
function builtAsStandard(c: Cabinet): boolean {
  return c.build === 'rigid' && !c.shelvesCustom && !c.drawersCustom;
}

function shelvesFit(c: Cabinet, p: CabinetProduct): boolean {
  if (c.type === 'drawers') return true;
  return c.shelves.length === p.shelves || c.shelves.length === defaultShelfCount(c);
}

function frontCount(p: CabinetProduct): number {
  return p.fronts.reduce((s, f) => s + f.quantity, 0);
}

function drawerDistance(p: CabinetProduct, c: Cabinet): number {
  const theirs = p.fronts.flatMap((f) => Array.from({ length: f.quantity }, () => f.height)).sort((a, b) => a - b);
  const ours = [...c.drawerFronts].sort((a, b) => a - b);
  return theirs.reduce((s, h, i) => s + Math.abs(h - (ours[i] ?? 0)), 0);
}

/** The unit's shape and fronts are the cabinet's. */
function sameShape(c: Cabinet, p: CabinetProduct): boolean {
  const frontH = c.h - RULES.frontHeightLess;
  switch (limitsKey(c.type, c.corner)) {
    case 'drawers':
      return (
        p.w === c.w && p.h === c.h && p.d === c.d && p.opening === 'drawers' &&
        frontCount(p) === c.drawerFronts.length && p.fronts.every((f) => f.width === c.w - RULES.frontWidthLess)
      );
    case 'tall': {
      if (p.w !== c.w || p.h !== c.h || p.d !== c.d || p.opening !== 'door') return false;
      const heights = [...new Set(p.fronts.map((f) => f.height))].sort((a, b) => a - b);
      if ((c.upper ?? 0) > 0) {
        // Upper and lower doors: two heights, the smaller the upper door, as many of each as across.
        return heights.length === 2 && heights[0] === c.upper && p.fronts.every((f) => f.quantity === c.doors);
      }
      return heights.length === 1 && heights[0] === frontH && frontCount(p) === c.doors;
    }
    case 'corner-l':
      return p.w === c.w && p.d === c.b && p.h === c.h && p.armDepth === c.d;
    case 'corner-blind':
      return p.w === c.w && p.d === c.d && p.h === c.h && p.blank?.side === c.blind && p.blank.mm === c.blank;
    case 'corner-diag':
      // The builder's diagonal corner is square: its width runs along both walls.
      return p.w === c.w && p.d === c.w && p.h === c.h && p.armDepth === c.d;
    default:
      return (
        p.w === c.w && p.h === c.h && p.d === c.d && p.opening === 'door' &&
        frontCount(p) === c.doors && p.fronts.every((f) => f.height === frontH)
      );
  }
}

/**
 * The standard unit this cabinet is, among its group's products, or undefined.
 * Where several share every measure the closest is taken: the chosen shelf
 * count first, then for drawers the fronts nearest the builder's, then the
 * plainest name (a base unit before the same unit "for pull-out bin"). Only a
 * unit on sale counts.
 */
export function matchStandard(c: Cabinet, products: readonly CabinetProduct[]): CabinetProduct | undefined {
  if (!standardGroupFor(c) || !builtAsStandard(c)) return undefined;
  const candidates = products.filter(
    (p) => p.available && p.price !== undefined && p.productId !== undefined && sameShape(c, p) && shelvesFit(c, p),
  );
  const score = (p: CabinetProduct) => [
    c.type === 'drawers' || c.shelves.length === p.shelves ? 0 : 1,
    // Where names are all that differ, one door is a single unit and two a double.
    /\bdouble\b/i.test(p.name) === (c.doors === 1) ? 1 : 0,
    c.type === 'drawers' ? drawerDistance(p, c) : 0,
    p.name.length,
  ];
  return [...candidates].sort((a, b) => {
    const [x, y] = [score(a), score(b)];
    for (let i = 0; i < x.length; i++) if (x[i] !== y[i]) return x[i]! - y[i]!;
    return a.sku.localeCompare(b.sku);
  })[0];
}

/**
 * The fronts a standard unit takes, as door-builder pieces with standard
 * drilling: the supplier's own front sizes, not the builder's arithmetic.
 * Quantities are for `qty` cabinets. A pair is hinged one each side; a single
 * door on the side the shopper chose, or beside a blind corner's blank.
 */
export function standardFronts(unit: CabinetProduct, c: Cabinet, handle: HandleProduct): Piece[] {
  if (!c.fronts) return [];
  if (unit.opening === 'drawers') {
    return unit.fronts.map((f) => newPiece('drawer', f.height, f.width, 'none', f.quantity * c.qty, handle));
  }
  const single = c.type === 'corner' && c.corner === 'blind' ? (c.blind === 'left' ? 'right' : 'left') : c.hinge;
  return unit.fronts.flatMap((f) => {
    if (f.quantity === 1) return [newPiece('door', f.height, f.width, single, c.qty, handle)];
    const left = Math.ceil(f.quantity / 2);
    return [
      newPiece('door', f.height, f.width, 'left', left * c.qty, handle),
      newPiece('door', f.height, f.width, 'right', (f.quantity - left) * c.qty, handle),
    ];
  });
}
